import { useTranslation } from "react-i18next";
import { EditIcon } from "@/components/icons/action-icons";
import {
  AI_ASST_MSG_TYPES,
  type IChatHistoryItem,
  type ISingleChatBubble,
} from "@/types/(my-space)/ai-assistant.interface";
import { BubbleAttachments } from "@/components/ai-assistant/attachment-chips";
import { GeneratedAttachments } from "@/components/ai-assistant/generated-attachments";
import { cn } from "@/utils/cn";
import { motion } from "framer-motion";
import { Typewriter } from "./typewriter";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type Ref,
  type RefObject,
  type SetStateAction,
} from "react";
import { useCopyToClipboard } from "usehooks-ts";
import toast from "react-hot-toast";
import {
  Check,
  Copy,
  Download,
  ListChecks,
  RefreshCw,
  Volume2,
  VolumeX,
} from "lucide-react";
import { speak, stripMarkdownToText } from "@/utils/notifications.util";
import { downloadAiMessagePdf } from "@/services/(my-space)/aiAssistantPdfDownload";
import { BlocksShuffleSvg } from "@/assets/svgs";
import type { ComponentProps } from "react";
import { messageHasDownloadable } from "./message-content";
import { PdfDownloadable } from "./pdf-downloadable";
import {
  PdfExportContext,
  usePdfExportValue,
  usePdfPartIndex,
} from "./pdf-export-context";
import { MarkdownImage, MarkdownLink } from "./markdown-media";
import { useOnClickOutside } from "usehooks-ts";
import { useEditMessageMutation } from "@/services/(my-space)/api.ai-assistant";
import { useAIAssistantContext } from "./context";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { loadMermaid } from "@/utils/load-mermaid";
import { detectFencedForm } from "@/components/ai-assistant/maya-form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type StepEntry = { message: string; at: string };

// Supplies the throttled scroll callback to deeply-nested MermaidChart without
// threading it through props — keeps MarkdownMessage's prop surface = { content }.
const ChartRenderContext = createContext<(() => void) | undefined>(undefined);

// Hide any ```maya-form JSON fence behind a friendly note so users never see
// the raw spec; the form itself renders above the composer (see prompt-input).
const stripMayaForm = (content: string) => detectFencedForm(content).content;

const MermaidChart = ({ chart }: { chart: string }) => {
  const { t } = useTranslation(["my-space", "common"]);
  const idRef = useRef(`mermaid-${Math.random().toString(36).slice(2)}`);
  const [svg, setSvg] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const onRender = useContext(ChartRenderContext);
  // Claim this chart's position once on mount (survives the svg-ready re-render).
  const chartIndex = usePdfPartIndex("chart");

  useEffect(() => {
    let cancelled = false;
    loadMermaid()
      .then((mermaid) => mermaid.render(idRef.current, chart))
      .then(({ svg: rendered }) => {
        if (cancelled) return;
        setSvg(rendered);
        onRender?.();
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });
    return () => {
      cancelled = true;
      // Drop mermaid's orphaned measurement nodes (its wrapper is "d"+id, the
      // inner svg is the id) if the row recycled mid-render.
      document.getElementById("d" + idRef.current)?.remove();
      document.getElementById(idRef.current)?.remove();
    };
  }, [chart]);

  if (hasError)
    return (
      <pre className="text-xs text-red-500 whitespace-pre-wrap">{chart}</pre>
    );
  if (!svg)
    return (
      <div className="text-xs text-slate-400 py-2 animate-pulse">
        {t("my-space:ui.rendering_chart")}
      </div>
    );
  return (
    <PdfDownloadable part="chart" index={chartIndex} className="my-2">
      <div
        dangerouslySetInnerHTML={{ __html: svg }}
        className="overflow-x-auto py-2"
      />
    </PdfDownloadable>
  );
};

// Standalone so it can use hooks: claims its 0-based table position on mount
// (source order) for the server-side per-table PDF export.
const TableBlock = ({ ...props }: ComponentProps<"table">) => {
  const tableIndex = usePdfPartIndex("table");
  return (
    <PdfDownloadable part="table" index={tableIndex} className="my-2">
      <div className="overflow-x-auto">
        <table className="border-collapse text-xs w-full" {...props} />
      </div>
    </PdfDownloadable>
  );
};

interface IProps {
  firstItemIndex: number;
  hasMore: boolean;
  sessionId: string | null;
  onStartReached: () => void;
  history: IChatHistoryItem[];
  isFetchingNextPage?: boolean;
  highlightMessageId: number | null;
  setHighlightMessageId: Dispatch<SetStateAction<number | null>>;
  setHistory: Dispatch<SetStateAction<IChatHistoryItem[]>>;
  statusLineRef?: Ref<StatusLineHandle>;
  stepsMap?: Record<number, StepEntry[]>;
  onRegenerate?: (answerId: number) => void;
}

export interface ChatHandle {
  scrollToBottom: () => void;
}

const REMARK_PLUGINS = [remarkGfm];

// Hoisted to module scope: the (~25-fn) components map + plugins are built once
// instead of per render, which lets React.memo(MarkdownMessage) actually hold.
const MARKDOWN_COMPONENTS: Components = {
  table: (props) => <TableBlock {...props} />,
  thead: ({ ...props }) => <thead className="bg-slate-100" {...props} />,
  th: ({ ...props }) => (
    <th
      className="border border-slate-200 px-3 py-1.5 text-start font-semibold text-slate-700"
      {...props}
    />
  ),
  td: ({ ...props }) => (
    <td
      className="border border-slate-200 px-3 py-1.5 text-slate-600"
      {...props}
    />
  ),
  pre: ({ ...props }) => (
    <pre
      className="bg-slate-100 rounded-lg p-3 text-xs overflow-x-auto my-2 font-mono"
      {...props}
    />
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className?.startsWith("language-"));
    const lang = className?.replace("language-", "") ?? "";
    if (isBlock && lang === "mermaid") {
      return <MermaidChart chart={String(children).trim()} />;
    }
    if (isBlock) {
      return (
        <code className={cn("font-mono text-xs", className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="bg-slate-100 rounded px-1 py-0.5 text-xs font-mono text-slate-800"
        {...props}
      >
        {children}
      </code>
    );
  },
  p: ({ ...props }) => (
    <p className="mb-1.5 last:mb-0 text-slate-700" {...props} />
  ),
  h1: ({ ...props }) => (
    <h1 className="text-base font-bold mb-1 mt-2 text-slate-800" {...props} />
  ),
  h2: ({ ...props }) => (
    <h2 className="text-sm font-bold mb-1 mt-2 text-slate-800" {...props} />
  ),
  h3: ({ ...props }) => (
    <h3 className="text-sm font-semibold mb-1 text-slate-800" {...props} />
  ),
  ul: ({ ...props }) => (
    <ul
      className="list-disc list-inside mb-1.5 space-y-0.5 text-slate-700"
      {...props}
    />
  ),
  ol: ({ ...props }) => (
    <ol
      className="list-decimal list-inside mb-1.5 space-y-0.5 text-slate-700"
      {...props}
    />
  ),
  li: ({ ...props }) => <li className="text-sm text-slate-700" {...props} />,
  blockquote: ({ ...props }) => (
    <blockquote
      className="border-s-2 border-slate-300 ps-3 my-2 text-slate-500 italic"
      {...props}
    />
  ),
  a: MarkdownLink,
  img: MarkdownImage,
  hr: () => <hr className="my-3 border-slate-200" />,
  strong: ({ ...props }) => (
    <strong className="font-semibold text-slate-800" {...props} />
  ),
  em: ({ ...props }) => <em className="italic text-slate-600" {...props} />,
};

const MarkdownMessage = memo(
  ({ content, messageId }: { content: string; messageId: number | null }) => {
    // Per-message counters so each table/chart gets a stable 0-based index that
    // matches the backend's source-order parse of the same markdown.
    const pdfCtx = usePdfExportValue(messageId, content);
    return (
      <PdfExportContext.Provider value={pdfCtx}>
        <div className="text-sm text-start leading-relaxed">
          {/* Keyed by content so a content change fully remounts the subtree and
              every table/chart re-claims its index from the reset counters. */}
          <ReactMarkdown
            key={content}
            remarkPlugins={REMARK_PLUGINS}
            components={MARKDOWN_COMPONENTS}
          >
            {content}
          </ReactMarkdown>
        </div>
      </PdfExportContext.Provider>
    );
  },
);
MarkdownMessage.displayName = "MarkdownMessage";

interface ChatRowProps {
  msg: ISingleChatBubble;
  isLast: boolean;
  isEditing: boolean;
  isCopied: boolean;
  isDownloading: boolean;
  shouldHighlight: boolean;
  shouldAnimateLast: boolean;
  isUpdatingMessage: boolean;
  steps?: StepEntry[];
  statusLineRef?: Ref<StatusLineHandle>;
  editMessageRef: RefObject<HTMLTextAreaElement | null>;
  onStartEdit: (id: number) => void;
  onCancelEdit: () => void;
  onSubmitEdit: (id: number, value: string) => void;
  onCopy: (content: string, id: number) => void;
  onRegenerate?: (id: number) => void;
  onDownloadPdf: (id: number) => void;
  onTypewriterComplete: () => void;
  onHighlightComplete: () => void;
  onChunk: () => void;
}

// One chat bubble. Memoized so a parent re-render (copy/edit/download/highlight
// state change, message append) only re-renders the row whose props actually
// changed — every other visible row is skipped. All handlers it receives are
// stabilized in Chat, so the shallow prop compare holds.
const ChatRow = memo(function ChatRow({
  msg,
  isLast,
  isEditing,
  isCopied,
  isDownloading,
  shouldHighlight,
  shouldAnimateLast,
  isUpdatingMessage,
  steps,
  statusLineRef,
  editMessageRef,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  onCopy,
  onRegenerate,
  onDownloadPdf,
  onTypewriterComplete,
  onHighlightComplete,
  onChunk,
}: ChatRowProps) {
  const { t } = useTranslation("my-space");
  const isUserBubble = msg.messageType === AI_ASST_MSG_TYPES.user;
  const isLoading = msg.content === "...";
  // Assistant bubbles render with the maya-form JSON fence stripped out.
  const displayContent = isUserBubble
    ? msg.content
    : stripMayaForm(msg.content);
  const CopyIcon = isCopied ? Check : Copy;

  // Per-message text-to-speech. `speak()` cancels any prior utterance, so
  // starting one message stops another mid-read — the onEnd callback flips the
  // stopped row back to idle. A ref lets the unmount cleanup stop speech only
  // when *this* (speaking) row leaves the DOM, e.g. scrolled out of the list.
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speakingRef = useRef(isSpeaking);
  speakingRef.current = isSpeaking;
  const handleReadAloud = useCallback(() => {
    if (speakingRef.current) {
      window.speechSynthesis?.cancel();
      setIsSpeaking(false);
      return;
    }
    const text = stripMarkdownToText(displayContent);
    if (!text) return;
    setIsSpeaking(true);
    speak(text, { onEnd: () => setIsSpeaking(false) });
  }, [displayContent]);
  useEffect(
    () => () => {
      if (speakingRef.current) window.speechSynthesis?.cancel();
    },
    [],
  );

  return (
    <motion.div
      initial={isLast ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex w-full px-4 py-1 ${isUserBubble ? "justify-end" : "justify-start"}`}
    >
      <motion.div
        animate={shouldHighlight ? { scale: [1, 1.02, 1] } : undefined}
        transition={{ duration: 0.4, ease: "easeOut" }}
        onAnimationComplete={() => {
          if (shouldHighlight) onHighlightComplete();
        }}
        className={cn(
          "group/row flex gap-2 items-end",
          isUserBubble ? "max-w-[50%]" : "max-w-[90%] md:max-w-[60%]",
        )}
      >
        <div
          className={cn("flex flex-col min-w-0", isUserBubble && "items-end")}
        >
          {isUserBubble && msg.attachments && msg.attachments.length > 0 && (
            <BubbleAttachments
              attachments={msg.attachments}
              variant="assistant"
              align="end"
              className={msg.content ? "mb-2" : ""}
            />
          )}
          {(!isUserBubble || msg.content) && (
            <div
              className={cn(
                "relative group/bubble rounded-xl px-4 py-2 text-sm text-start",
                isUserBubble
                  ? "rounded-tr-none bg-primary text-white whitespace-pre-wrap"
                  : "rounded-tl-none border border-slate-200 bg-white shadow-xs",
              )}
            >
              {/* Edit + copy actions (user bubble only) */}
              {isUserBubble && (
                <div className="hidden group-hover/bubble:flex gap-1 absolute top-2 -start-16 place-items-center text-primary px-4">
                  <button type="button" onClick={() => onStartEdit(msg.id)}>
                    <EditIcon size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onCopy(msg.content, msg.id)}
                  >
                    <CopyIcon size={20} />
                  </button>
                </div>
              )}

              {isEditing ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const val = editMessageRef.current?.value;
                    if (val?.trim()) onSubmitEdit(msg.id, val);
                    else toast.error(t("toasts.message_cannot_be_empty"));
                  }}
                >
                  <textarea
                    rows={3}
                    autoFocus
                    ref={editMessageRef}
                    defaultValue={msg.content}
                    className="w-full bg-white/10 text-white rounded p-2 border border-white/20 focus:outline-hidden"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.form?.requestSubmit();
                      }
                    }}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={onCancelEdit}
                      className="text-xs opacity-70 hover:opacity-100"
                    >
                      {t("ui.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={isUpdatingMessage}
                      className="text-xs bg-white text-primary px-2 py-1 rounded font-bold"
                    >
                      {isUpdatingMessage ? t("ui.saving_2") : t("ui.save")}
                    </button>
                  </div>
                </form>
              ) : isLoading ? (
                <div className="flex flex-col gap-1">
                  <TypingDots />
                  {isLast && (
                    <StatusLine ref={statusLineRef} initial="Thinking…" />
                  )}
                </div>
              ) : !isUserBubble && isLast && shouldAnimateLast ? (
                // Animate plain text (cheap) — markdown/mermaid is parsed once on
                // completion, not re-parsed on every tick.
                <Typewriter
                  key={displayContent}
                  text={displayContent}
                  onChunk={onChunk}
                  onComplete={onTypewriterComplete}
                >
                  {(text) => (
                    <div className="text-sm text-start leading-relaxed whitespace-pre-wrap">
                      {text}
                    </div>
                  )}
                </Typewriter>
              ) : !isUserBubble ? (
                <MarkdownMessage content={displayContent} messageId={msg.id} />
              ) : (
                msg.content
              )}
            </div>
          )}

          {!isLoading && !isUserBubble && (
            <GeneratedAttachments
              attachments={msg.generatedAttachments}
              errors={msg.attachmentErrors}
              className="mt-2"
            />
          )}

          {/* Bottom action bar — AI messages only */}
          {!isLoading && !isUserBubble && (
            <div className="flex gap-1.5 mt-1 px-1">
              <button
                type="button"
                title={isCopied ? t("ui.copied_2") : t("ui.copy")}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                onClick={() => onCopy(displayContent, msg.id)}
              >
                {isCopied ? (
                  <Check size={11} className="text-green-500" />
                ) : (
                  <Copy size={11} />
                )}
                {isCopied ? t("ui.copied") : t("ui.copy")}
              </button>
              <button
                type="button"
                title={isSpeaking ? t("ui.stop") : t("ui.read_aloud")}
                className={cn(
                  "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border bg-white transition-colors",
                  isSpeaking
                    ? "text-primary border-primary/40 hover:bg-primary/5"
                    : "text-slate-500 hover:text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50",
                )}
                onClick={handleReadAloud}
              >
                {isSpeaking ? <VolumeX size={11} /> : <Volume2 size={11} />}
                {isSpeaking ? t("ui.stop") : t("ui.read_aloud")}
              </button>
              <button
                type="button"
                title={t("labels.regenerate")}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                onClick={() => onRegenerate?.(msg.id)}
              >
                <RefreshCw size={11} />
                {t("ui.regenerate")}
              </button>
              {/* Hidden mid-animation: the bubble holds plain text until the
                  typewriter settles, so an export then would snapshot unstyled text. */}
              {messageHasDownloadable(msg.content) &&
                !(isLast && shouldAnimateLast) && (
                  <button
                    type="button"
                    title={t("labels.save_as_pdf")}
                    disabled={isDownloading}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors disabled:opacity-60"
                    onClick={() => onDownloadPdf(msg.id)}
                  >
                    <Download size={11} />
                    {isDownloading ? t("ui.saving_2_2") : t("ui.save_pdf")}
                  </button>
                )}
              {(steps?.length ?? 0) > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      title={t("labels.view_steps")}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                    >
                      <ListChecks size={11} />
                      {t("ui.steps")}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="start"
                    className="w-80 p-3 max-h-72 overflow-y-auto"
                  >
                    <p className="text-xs font-semibold text-slate-700 mb-3">
                      {t("ui.steps_taken")}
                    </p>
                    <div className="flex flex-col">
                      {steps!.map((step, i, arr) => (
                        <div key={i} className="flex gap-2 items-start">
                          <div className="flex flex-col items-center shrink-0">
                            <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold">
                              {i + 1}
                            </div>
                            {i < arr.length - 1 && (
                              <div className="w-px h-4 bg-slate-200 my-0.5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pb-3">
                            <p className="text-xs text-slate-700 leading-snug">
                              {step.message}
                            </p>
                            {step.at && (
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                {step.at}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
});
ChatRow.displayName = "ChatRow";

export const Chat = forwardRef<ChatHandle, IProps>(
  (
    {
      history,
      sessionId,
      setHistory,
      firstItemIndex,
      hasMore,
      onStartReached,
      highlightMessageId,
      isFetchingNextPage,
      setHighlightMessageId,
      statusLineRef,
      stepsMap,
      onRegenerate,
    },
    ref,
  ) => {
    const { t } = useTranslation("my-space");
    const { responseMessageTypeRef } = useAIAssistantContext();
    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const messages = useMemo(
      () => history.flatMap((item) => [item.question, item.answer]),
      [history],
    );

    const [shouldAnimateLast, setShouldAnimateLast] = useState(false);

    useEffect(() => {
      if (
        responseMessageTypeRef.current === "new_response" ||
        responseMessageTypeRef.current === "edit"
      ) {
        setShouldAnimateLast(true);
      } else {
        setShouldAnimateLast(false);
      }
    }, [history]);

    // Scroll to bottom on session change
    const prevSessionIdRef = useRef<string | null>(null);
    useEffect(() => {
      const hasChatChanged = prevSessionIdRef.current !== sessionId;
      if (hasChatChanged && messages.length > 0) {
        virtuosoRef.current?.scrollToIndex({
          index: messages.length - 1,
          behavior: "auto",
        });
        prevSessionIdRef.current = sessionId;
      }
    }, [sessionId, messages]);

    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        virtuosoRef.current?.scrollToIndex({
          index: messages.length - 1,
          behavior: "auto",
        });
      },
    }));

    // Highlight scroll
    useEffect(() => {
      if (highlightMessageId) {
        const idx = messages.findIndex((m) => m.id === highlightMessageId);
        if (idx !== -1) {
          virtuosoRef.current?.scrollToIndex({
            index: idx,
            behavior: "smooth",
            align: "center",
          });
        }
      }
    }, [highlightMessageId]);

    // Mirror messages in a ref so stable callbacks (scrollToBottom, edit) read
    // the latest array without being recreated when history changes.
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const rafRef = useRef<number | null>(null);
    const scrollToBottom = useCallback(() => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index: messagesRef.current.length - 1,
          behavior: "auto",
        });
        rafRef.current = null;
      });
    }, []);

    const [editingMessageId, setEditingMessageId] = useState<number | null>(
      null,
    );
    const cancelEdit = useCallback(() => setEditingMessageId(null), []);

    const [editMessage, { isLoading: isUpdatingMessage }] =
      useEditMessageMutation();
    const handleEditMessage = useCallback(
      async (messageId: number, newContent: string) => {
        if (!newContent.trim()) return;
        await editMessage({ messageId, content: newContent })
          .unwrap()
          .then((res) => {
            responseMessageTypeRef.current = "edit";
            toast.success(res?.message || "Message updated");
            const responseData = res.data;
            const msgs = messagesRef.current;
            const isLastExchange = msgs[msgs.length - 2]?.id === messageId;
            if (isLastExchange) setShouldAnimateLast(true);
            setHistory((prev) =>
              prev.map((item): IChatHistoryItem => {
                if (item.question.id === messageId) {
                  return {
                    ...item,
                    question: { ...item.question, content: newContent },
                    answer: {
                      ...item.answer,
                      id: responseData.answerId || item.answer.id,
                      content: responseData.answer || item.answer.content,
                      generatedAttachments:
                        responseData.generatedAttachments ?? [],
                      attachmentErrors: responseData.attachmentErrors ?? [],
                    },
                  };
                }
                return item;
              }),
            );
            cancelEdit();
          })
          .catch(() => toast.error(t("toasts.failed_to_edit_message")));
      },
      [editMessage, setHistory, cancelEdit, responseMessageTypeRef, t],
    );

    const [, copy] = useCopyToClipboard();
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const handleCopyToClipboard = useCallback(
      (content: string, id: number) => {
        copy(content)
          .then(() => {
            setCopiedId(id);
            toast.success(t("toasts.copied_to_clipboard"), { id: "cc-toast" });
            setTimeout(
              () => setCopiedId((prev) => (prev === id ? null : prev)),
              2000,
            );
          })
          .catch(() =>
            toast.error(t("toasts.failed_to_copy"), { id: "cc-toast" }),
          );
      },
      [copy, t],
    );

    // downloadingId drives the button label; the ref guards against concurrent
    // downloads without making handleDownloadPdf identity depend on it.
    const downloadingIdRef = useRef<number | null>(null);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const handleDownloadPdf = useCallback(
      async (id: number) => {
        if (downloadingIdRef.current !== null) return;
        downloadingIdRef.current = id;
        setDownloadingId(id);
        try {
          await toast.promise(
            downloadAiMessagePdf({ messageId: id, part: "full" }),
            {
              loading: t("ui.generating_pdf"),
              success: t("ui.pdf_saved"),
              error: (e) =>
                (e as Error)?.message ?? t("toasts.failed_to_save_pdf"),
            },
            { id: `pdf-${id}` },
          );
        } catch {
          // toast.promise already surfaces the error
        } finally {
          downloadingIdRef.current = null;
          setDownloadingId(null);
        }
      },
      [t],
    );

    const onTypewriterComplete = useCallback(() => {
      setShouldAnimateLast(false);
      responseMessageTypeRef.current = "initial";
    }, [responseMessageTypeRef]);

    const onHighlightComplete = useCallback(
      () => setHighlightMessageId(null),
      [setHighlightMessageId],
    );

    const editMessageRef = useRef<HTMLTextAreaElement>(null);
    useOnClickOutside(editMessageRef as React.RefObject<HTMLElement>, () =>
      cancelEdit(),
    );

    const itemContent = useCallback(
      (_virtuosoIndex: number, msg: ISingleChatBubble) => {
        const msgs = messagesRef.current;
        const isLast = msg.id === msgs[msgs.length - 1]?.id;
        const isEditing = editingMessageId === msg.id;
        return (
          <ChatRow
            key={msg.id}
            msg={msg}
            isLast={isLast}
            isEditing={isEditing}
            isCopied={copiedId === msg.id}
            isDownloading={downloadingId === msg.id}
            shouldHighlight={highlightMessageId === msg.id}
            // Gate per-row so flipping these only re-renders the affected row.
            shouldAnimateLast={isLast && shouldAnimateLast}
            isUpdatingMessage={isEditing && isUpdatingMessage}
            steps={stepsMap?.[msg.id]}
            statusLineRef={statusLineRef}
            editMessageRef={editMessageRef}
            onStartEdit={setEditingMessageId}
            onCancelEdit={cancelEdit}
            onSubmitEdit={handleEditMessage}
            onCopy={handleCopyToClipboard}
            onRegenerate={onRegenerate}
            onDownloadPdf={handleDownloadPdf}
            onTypewriterComplete={onTypewriterComplete}
            onHighlightComplete={onHighlightComplete}
            onChunk={scrollToBottom}
          />
        );
      },
      [
        highlightMessageId,
        shouldAnimateLast,
        editingMessageId,
        copiedId,
        downloadingId,
        isUpdatingMessage,
        stepsMap,
        statusLineRef,
        cancelEdit,
        handleEditMessage,
        handleCopyToClipboard,
        onRegenerate,
        handleDownloadPdf,
        onTypewriterComplete,
        onHighlightComplete,
        scrollToBottom,
      ],
    );

    return (
      <ChartRenderContext.Provider value={scrollToBottom}>
        <Virtuoso
          ref={virtuosoRef}
          className="w-full h-full scrollbar-hidden"
          data={messages}
          firstItemIndex={firstItemIndex}
          initialTopMostItemIndex={Math.max(0, messages.length - 1)}
          followOutput={(isAtBottom) => (isAtBottom ? "smooth" : false)}
          startReached={() => {
            if (hasMore && !isFetchingNextPage) onStartReached();
          }}
          overscan={300}
          components={{
            Header: () => (
              <div className="flex justify-center pt-4">
                {isFetchingNextPage && <Spinner />}
              </div>
            ),
            Footer: () => <div className="h-4" />,
          }}
          itemContent={itemContent}
        />
      </ChartRenderContext.Provider>
    );
  },
);

Chat.displayName = "Chat";

export interface StatusLineHandle {
  setStatus: (message: string) => void;
}

// Streaming status line owns its own state and is updated imperatively via ref,
// so SSE status ticks re-render only this leaf — never the page or the chat list.
export const StatusLine = forwardRef<StatusLineHandle, { initial?: string }>(
  ({ initial = "" }, ref) => {
    const [status, setStatus] = useState(initial);
    useImperativeHandle(ref, () => ({ setStatus }), []);
    if (!status) return null;
    return <p className="text-xs text-slate-400 animate-pulse">{status}</p>;
  },
);
StatusLine.displayName = "StatusLine";

export const TypingDots = () => {
  const { t } = useTranslation("my-space");

  return (
    <div
      className="py-1 text-slate-400"
      role="status"
      aria-label={t("labels.thinking")}
    >
      <BlocksShuffleSvg className="size-6" />
    </div>
  );
};
